import { Box } from "@mui/material";
import { useIntl } from "react-intl";
import { JobDataSummary } from "services/KnowfullyAdminClient";
import { containsGeneratedId, parseGeneratedId } from "./importEntityLinks";

type ImportsSummaryMessagesProps = {
  summary: JobDataSummary;
};

const styles = {
  panel: {
    display: "grid",
    gridTemplateColumns: "minmax(120px, max-content) 1fr",
    gap: "6px 24px",
    py: 1.5,
    pl: 2,
  },
  label: {
    fontSize: "0.68rem",
    letterSpacing: "0.09em",
    color: "text.secondary",
    fontWeight: 600,
    pt: "2px",
  },
  value: {
    fontFamily: "monospace",
    fontSize: "0.78rem",
    wordBreak: "break-word",
  },
  extra: {
    gridColumn: "1 / -1",
    fontFamily: "monospace",
    fontSize: "0.75rem",
    color: "text.secondary",
  },
};

export const ImportsSummaryMessages = ({
  summary,
}: ImportsSummaryMessagesProps) => {
  const { formatMessage: t } = useIntl();

  const generatedId = parseGeneratedId(summary.details);

  const rows: { labelKey: string; value?: string }[] = [
    { labelKey: "rowNumberLabel", value: summary.rowNumber?.toString() },
    { labelKey: "typeLabel", value: summary.jobDataType },
    { labelKey: "statusLabel", value: summary.jobDataStatus },
    { labelKey: "generatedIdLabel", value: generatedId },
    { labelKey: "messageLabel", value: summary.message },
  ].filter((row) => !!row.value);

  // Only details the rows above don't already say. The job-data message and the generated-id marker each
  // get their own row, so repeating them underneath is noise.
  const remaining = (summary.details ?? []).filter(
    (detail) =>
      !!detail && detail !== summary.message && !containsGeneratedId(detail)
  );

  return (
    <Box sx={styles.panel}>
      {rows.map((row) => (
        <Box key={row.labelKey} sx={{ display: "contents" }}>
          <Box sx={styles.label}>{t({ id: row.labelKey }).toUpperCase()}</Box>
          <Box sx={styles.value}>{row.value}</Box>
        </Box>
      ))}
      {remaining.map((detail, index) => (
        <Box key={`${detail}-${index}`} sx={styles.extra}>
          {detail}
        </Box>
      ))}
    </Box>
  );
};
